import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  CCS_BUFFER_COMMON_OUTCOME_NOTE,
  CCS_BUFFER_DISCLAIMER,
  balancingHeadline,
  bufferBullet,
  confirmationWindowBullet,
  formatAud,
  recoveryBullet,
  sourceBullet,
  withholdingLeverBullet,
  ccsBufferRulesFor,
} from "@aucash/rules/ccs-buffer";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput, Segmented, Stepper } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// ccs-buffer — the CCS Withholding & Balancing-Debt Risk Check, surfaced
// stateless-v1 (PLAN arch decision #1). One screen; the result renders INLINE
// (no save-by-id, no auth gate, no persistence). Each compute is a stateless
// `calc.ccsBuffer.compute` query — input -> output, no round-trip to a stored
// row. The module is a forward-projection over the SHIPPED au13 CCS engine.
//
// Copy comes verbatim from the rules layer's copy helpers so the wording can
// never drift from the API/SEO surfaces (validation §3: information, not advice;
// the output is the family's OWN modelled estimate that Services Australia
// FINALISES — NEVER an approval; no "you should withhold"/"save"). The
// rules-layer guard already strips any financial-product/affiliate term from the
// output we render.

const ASSESSMENT_YEAR = "2025-26";

const CARE_TYPE_OPTIONS = [
  { value: "centre_based_day_care", label: "Centre-based" },
  { value: "family_day_care", label: "Family day care" },
  { value: "outside_school_hours_care", label: "OSHC" },
  { value: "in_home_care", label: "In-home care" },
] as const;
type CareType = (typeof CARE_TYPE_OPTIONS)[number]["value"];

const WITHHOLDING_OPTIONS = [
  { value: "5", label: "5% (default)" },
  { value: "10", label: "10%" },
  { value: "15", label: "15%" },
  { value: "25", label: "25%" },
  { value: "0", label: "0%" },
] as const;

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.ccsBuffer.compute.query>
>;

export default function CcsBufferIndex(): JSX.Element {
  const router = useRouter();

  const [lodgedIncome, setLodgedIncome] = useState("");
  const [projectedIncome, setProjectedIncome] = useState("");
  const [withholdingPct, setWithholdingPct] = useState("5");
  const [careType, setCareType] = useState<CareType>("centre_based_day_care");
  const [hourlyFee, setHourlyFee] = useState("");
  const [hoursPerFortnight, setHoursPerFortnight] = useState(72);
  const [fortnights, setFortnights] = useState(26);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (lodgedIncome.trim() === "")
      next.lodged = "Enter the family income your CCS was paid on.";
    if (projectedIncome.trim() === "")
      next.projected = "Enter your best estimate of your actual income this year.";
    if (hourlyFee.trim() === "")
      next.fee = "Enter the hourly fee your service charges.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.ccsBuffer.compute
      .query({
        input: {
          lodgedIncomeEstimate: num(lodgedIncome),
          projectedActualIncome: num(projectedIncome),
          currentWithholdingRate: num(withholdingPct) / 100,
          fortnightsInCare: fortnights,
          careLines: [
            {
              hoursUsed: hoursPerFortnight,
              careType,
              hourlyFee: num(hourlyFee),
              activityHours: 0,
              schoolAged: false,
              indigenousChild: false,
              childRank: 1,
            },
          ],
          assessmentYear: ASSESSMENT_YEAR,
        },
      })
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="CCS buffer" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · childcare subsidy</Eyebrow>
            <Text style={styles.title}>Heading for a childcare subsidy debt?</Text>
            <Text style={styles.subtitle}>
              Your Child Care Subsidy is paid on your estimated income, then
              reconciled against your actual income at balancing. If your income
              rose, you may be overpaid and face a balancing debt; if it fell,
              you may receive a top-up. This models your position from the income
              you enter — Services Australia determines the final amount.
            </Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Income your CCS was paid on (your lodged estimate)"
            help="The family income figure you gave Services Australia — the subsidy was paid on this."
            error={errors.lodged}
          >
            <MoneyInput
              value={lodgedIncome}
              onChange={setLodgedIncome}
              testID="input-lodged-income"
              accessibilityLabel="Income your CCS was paid on"
            />
          </Field>

          <Field
            label="Your best estimate of your actual income this year"
            help="Your lodged estimate plus any mid-year changes you know about. Balancing recomputes the subsidy against this."
            error={errors.projected}
          >
            <MoneyInput
              value={projectedIncome}
              onChange={setProjectedIncome}
              testID="input-projected-income"
              accessibilityLabel="Your best estimate of your actual income this year"
            />
          </Field>

          <Field label="Type of care">
            <Segmented<CareType>
              options={CARE_TYPE_OPTIONS}
              value={careType}
              onChange={setCareType}
              testIDPrefix="care-type"
            />
          </Field>

          <Field
            label="Hourly fee your service charges"
            help="The hourly fee on your statement. The subsidy is paid on the lower of this and the hourly rate cap for your care type."
            error={errors.fee}
          >
            <MoneyInput
              value={hourlyFee}
              onChange={setHourlyFee}
              testID="input-hourly-fee"
              accessibilityLabel="Hourly fee your service charges"
            />
          </Field>

          <Field label="Subsidised hours of care per fortnight">
            <Stepper
              value={hoursPerFortnight}
              onChange={setHoursPerFortnight}
              min={0}
              max={100}
              suffix="hrs"
              testID="input-hours"
              accessibilityLabel="Subsidised hours of care per fortnight"
            />
          </Field>

          <Field
            label="Withholding percentage you've set"
            help="Services Australia withholds a buffer of your subsidy (5% by default) and releases it at balancing if there's no debt. You can change it up to twice a year online through myGov."
          >
            <Segmented<string>
              options={WITHHOLDING_OPTIONS}
              value={withholdingPct}
              onChange={setWithholdingPct}
              testIDPrefix="withholding"
            />
          </Field>

          <Field label="Fortnights of subsidised care this year">
            <Stepper
              value={fortnights}
              onChange={setFortnights}
              min={1}
              max={26}
              suffix="fn"
              testID="input-fortnights"
              accessibilityLabel="Fortnights of subsidised care this year"
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="ccs-buffer-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer CCS_BUFFER_DISCLAIMER) sits at the TOP, before
 * any number (validation §3.2 placement rule). It states this is the family's own
 * modelled estimate, not a Services Australia approval. All wording comes from the
 * rules-layer copy helpers — no inline figures, no advice framing. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { output, input } = result;
  const rules = ccsBufferRulesFor(result.fy);

  const headline = balancingHeadline(output);
  const buffer = bufferBullet(output, input.currentWithholdingRate);
  const recovery = recoveryBullet(output, rules);
  const lever = withholdingLeverBullet(output, rules);
  const window = confirmationWindowBullet(rules);

  const chipTone = output.direction === "debt" ? "owed" : "est";
  const chipLabel =
    output.direction === "debt"
      ? "Possible debt"
      : output.direction === "top_up"
        ? "Possible top-up"
        : "Roughly square";

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. This is the
          family's own modelled estimate, NOT a Services Australia decision. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{CCS_BUFFER_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label={chipLabel} tone={chipTone} />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
      </Card>

      <Section title="How the withholding buffer applies">
        <Text style={styles.bodyText}>{buffer}</Text>
        {recovery !== null ? <Text style={styles.bodyText}>{recovery}</Text> : null}
      </Section>

      <Section title="Your options">
        {lever !== null ? <Text style={styles.bodyText}>{lever}</Text> : null}
        <Text style={styles.bodyText}>{window}</Text>
        <Text style={styles.bodyText}>{CCS_BUFFER_COMMON_OUTCOME_NOTE}</Text>
      </Section>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>{sourceBullet(rules)}</Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion} · CCS rates from au13 {result.au13Fy}
        </Text>
      </Section>
    </View>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline/bullet strings already embed the formatted figures from the rules
// layer, so it isn't called directly here. Referenced to keep the import
// meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
